import type { PublicPermitResponse } from '@contracts/order/PublicOrderResponse';
import { MobileContactCard } from '@src/authentication/MobileContactCard';
import { LogoAndBrand } from '@src/common/components/brand/LogoAndBrand';
import { Card } from '@src/common/components/Card';
import { LoadingPage } from '@src/common/components/LoadingPage';
import { MobileDetailItems } from '@src/common/components/mobile/MobileDetailItems';
import { MobileLayout } from '@src/common/components/mobile/MobileLayout';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import i18n from '@src/common/i18n/i18n';
import { convertClientLanguageToI18n } from '@src/common/i18n/LanguageConverter';
import { colors, typography } from '@src/common/styles/valueTokens';
import Config from '@src/config/Config';
import { useGetPublicOrder } from '@src/publicPortal/hooks/useGetPublicOrder';
import { PermitCard } from '@src/publicPortal/PermitCard';
import { PublicOrderResultType } from '@src/publicPortal/PublicOrderResult';
import { PublicPortalExpiredLink } from '@src/publicPortal/PublicPortalExpiredLink';
import { PublicPortalInvalidLink } from '@src/publicPortal/PublicPortalInvalidLink';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export const PublicPortal = () => {
  const { token } = useParams<{ token: string }>();

  if (!token) return <PublicPortalInvalidLink />;

  return <PublicPortalPageContent token={token} />;
};

const PublicPortalPageContent = ({ token }: { token: string }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetPublicOrder(token);

  const order =
    data?.type === PublicOrderResultType.OK ? data.order : undefined;

  useEffect(() => {
    if (order?.client.language) {
      const language = convertClientLanguageToI18n(order.client.language);
      i18n.changeLanguage(language);
    }
  }, [order?.client.language]);

  if (isLoading) return <LoadingPage />;
  if (isError || !data) return <PublicPortalInvalidLink />;
  if (data.type === PublicOrderResultType.EXPIRED)
    return <PublicPortalExpiredLink assignee={data.claimedBy} />;
  if (!order) return <PublicPortalInvalidLink />;

  const hasOrderDetails =
    order.tractorUnit ||
    order.tractorLicensePlate ||
    order.trailerLicensePlate ||
    order.trailerUnit;

  return (
    <MobileLayout>
      <PublicPortalHeaderCard
        orderNumber={order.orderDisplayId}
        client={order.client.name}
        effectiveDate={order.effectiveDate}
      />

      <MobileContactCard
        id={order.assignee?.id}
        name={order.assignee?.name}
        email={order.assignee?.email}
        extension={order.assignee?.extension}
      />
      {hasOrderDetails && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <PublicPortalOrderContent
            tractorLicensePlate={order.tractorLicensePlate}
            tractorUnit={order.tractorUnit}
            trailerLicensePlate={order.trailerLicensePlate}
            trailerUnit={order.trailerUnit}
          />
        </Stack>
      )}
      <Stack sx={{ mt: 2 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {t('publicPortal.permitsSection')}
        </Typography>
        <PublicPortalPermitContent permits={order.permits} />
      </Stack>
      <PublicPortalLegalNotice />
    </MobileLayout>
  );
};

interface PublicPortalHeaderCardProps {
  orderNumber: string;
  effectiveDate: string;
  client: string;
}
const PublicPortalHeaderCard = ({
  orderNumber,
  effectiveDate,
  client,
}: PublicPortalHeaderCardProps) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={1.5} sx={{ mb: 2 }}>
      <Stack alignItems="flex-end">
        <LogoAndBrand width={120} height={50} />
      </Stack>
      <Card
        sx={{
          p: 2,
          border: `1px solid ${colors.primary[40]}`,
          position: 'relative',
        }}
      >
        <Typography variant="h6">{client}</Typography>
        <Typography variant="subtitle2">
          {t('publicPortal.orderNumber', { displayId: orderNumber })}
        </Typography>
        <Typography variant="subtitle2">
          {t('publicPortal.effectiveOn', {
            effectiveDate: format(new Date(effectiveDate), 'yyyy-MM-dd'),
          })}
        </Typography>
      </Card>
    </Stack>
  );
};
interface PublicPortalOrderContentProps {
  tractorUnit?: number;
  tractorLicensePlate?: string;
  trailerUnit?: number;
  trailerLicensePlate?: string;
}
const PublicPortalOrderContent = ({
  tractorUnit,
  tractorLicensePlate,
  trailerUnit,
  trailerLicensePlate,
}: PublicPortalOrderContentProps) => {
  const { t } = useTranslation();

  const tractorItems = [
    ...(tractorUnit
      ? [{ label: t('publicPortal.unit'), value: tractorUnit }]
      : []),
    ...(tractorLicensePlate
      ? [{ label: t('publicPortal.plate'), value: tractorLicensePlate }]
      : []),
  ];

  const trailerItems = [
    ...(trailerUnit
      ? [{ label: t('publicPortal.unit'), value: trailerUnit }]
      : []),
    ...(trailerLicensePlate
      ? [{ label: t('publicPortal.plate'), value: trailerLicensePlate }]
      : []),
  ];

  return (
    <Stack>
      {!!tractorItems.length && (
        <Stack>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {t('publicPortal.tractorSection')}
          </Typography>
          <Card sx={{ p: 2, py: 1 }}>
            <MobileDetailItems items={tractorItems} />
          </Card>
        </Stack>
      )}
      {!!trailerItems.length && (
        <Stack sx={{ mt: 1 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {t('publicPortal.trailerSection')}
          </Typography>
          <Card sx={{ p: 2, py: 1 }}>
            <MobileDetailItems items={trailerItems} />
          </Card>
        </Stack>
      )}
    </Stack>
  );
};

const PublicPortalPermitContent = ({
  permits,
}: {
  permits: PublicPermitResponse[];
}) => {
  return (
    <Stack>
      {permits.map((permit) => (
        <PermitCard key={permit.id} permit={permit} />
      ))}
    </Stack>
  );
};

const PublicPortalLegalNotice = () => {
  const { t } = useTranslation();
  const brand = Config.VITE_BRAND;

  return (
    <Stack
      sx={{
        mt: 4,
        pt: 2,
        borderTop: `1px solid ${colors.neutral[90]}`,
        '& p': {
          ...typography.body.small,
          color: colors.neutral[60],
          marginBottom: 0.5,
        },
      }}
    >
      <p>{t('publicPortal.legalNotice.paragraph1', { brand })}</p>
      <p>{t('publicPortal.legalNotice.paragraph2', { brand })}</p>
      <p>
        {t('publicPortal.legalNotice.paragraph3Prefix', { brand })}
        <strong>{t('publicPortal.legalNotice.paragraph3Highlight')}</strong>
      </p>
    </Stack>
  );
};
